import { useState } from "react";
import { OcrModal } from "./ocrModal";
import SelectedCircleIcon from "@/assets/icons/selected-circle.svg?react";
import { GetAllExpeditionResponseDtoExpedition } from "@/types/api/api-types";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useValidateCustomQuotation } from "@/api/expedition.api";
import { InfoFile } from "@/components/shared/infoFile";
import { useTranslation } from "react-i18next";

type ModalProps = {
  expedition: GetAllExpeditionResponseDtoExpedition;
  onClose: () => void;
  navigate: (path: string) => void;
};

export const FinalizeModal = ({ expedition, onClose, navigate }: ModalProps) => {
  const { t } = useTranslation(["boExpeditions", "common"]);
  const [selected, setSelected] = useState<number | null>(null);
  const [ocrStep, setOcrStep] = useState<number | null>(null);
  const [newFilesId, setNewFilesId] = useState<string[]>([]);

  const { mutateAsync: validateCustomQuotation, isPending } = useValidateCustomQuotation();

  const handleValidateCustomQuotation = async () => {
    try {
      await validateCustomQuotation({
        id: expedition.id,
        payload: {
          newFilesId,
        },
      });
    } catch (error) {
      console.error("Error validating custom quotation:", error);
    }

    onClose();
  };

  const handleClick = async () => {
    if (selected === 1) {
      setOcrStep(0);
    } else if (selected === 2) {
      navigate(`/update-cotation/${expedition.id}`);
    }
  };

  if (expedition.customQuotation) {
    return (
      <dialog className="fixed top-0 left-0 z-50 flex h-full w-full items-center justify-center overflow-auto bg-[#00000080] backdrop-blur-sm">
        <div className="m-auto w-[700px] max-w-[90vw] rounded-lg bg-white">
          <div className="items- relative flex flex-col">
            <div className="flex flex-row items-center justify-between gap-4 p-4">
              <div />
              <h2 className="text-center text-2xl font-semibold text-black">
                {t("pendingShipmentsFinalizeModal.customQuotationTitle")}
              </h2>
              <button onClick={onClose} className="text-sm font-medium text-black">
                <X />
              </button>
            </div>
            <div className="w-full border-b border-solid border-gray-200" />
            <div className="flex flex-col gap-4 p-4">
              <InfoFile setNewFilesId={setNewFilesId} newFilesId={newFilesId} />
            </div>
            <div className="flex w-full flex-row justify-center gap-2 p-4">
              <Button
                className="flex-1"
                variant="outline"
                onClick={() => onClose()}
                disabled={isPending}
              >
                {t("common:actions.cancel")}
              </Button>
              <Button
                className="flex-1"
                onClick={() => handleValidateCustomQuotation()}
                disabled={isPending}
              >
                {isPending ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  t("pendingShipmentsFinalizeModal.validateQuote")
                )}
              </Button>
            </div>
          </div>
        </div>
      </dialog>
    );
  }

  if (ocrStep !== null && selected === 1) {
    return (
      <OcrModal
        onClose={() => {
          setSelected(null);
          setOcrStep(null);
        }}
        prev={() => {
          setSelected(null);
          setOcrStep(null);
        }}
        expedition={expedition}
      />
    );
  }

  return (
    <dialog className="fixed top-0 left-0 z-50 flex h-full w-full items-center justify-center overflow-auto bg-[#00000080] backdrop-blur-sm">
      <div className="m-auto w-[500px] max-w-[90vw] rounded-lg bg-white">
        <div className="items- relative flex flex-col">
          <div className="flex flex-row items-center justify-between gap-4 p-8">
            <div />
            <h2 className="text-center text-2xl font-semibold text-black">
              {t("pendingShipmentsFinalizeModal.quotationTitle")}
            </h2>
            <button onClick={onClose} className="text-sm font-medium text-black">
              <X />
            </button>
          </div>
          <div className="w-full border-b border-solid border-gray-200" />
          <div className="flex flex-col gap-8 p-8">
            <div className="flex flex-col gap-4">
              <FinalizationOption
                selected={selected === 1}
                setSelected={() => setSelected(1)}
                text={t("pendingShipmentsFinalizeModal.importInvoiceOption")}
              />
              <span className="text-center text-base font-medium text-gray-900">
                {t("pendingShipmentsFinalizeModal.or")}
              </span>
              <FinalizationOption
                selected={selected === 2}
                setSelected={() => setSelected(2)}
                text={t("pendingShipmentsFinalizeModal.fillFormOption")}
              />
            </div>
            <div className="flex w-full flex-row justify-center gap-2">
              <button
                className={`bg-brand700 hover:bg-brand600 flex-1 border-2 border-solid px-4 py-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-200 disabled:text-gray-400`}
                onClick={() => handleClick()}
                disabled={!selected}
              >
                {t("pendingShipmentsFinalizeModal.continue")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </dialog>
  );
};

type Props = {
  selected: boolean;
  setSelected: () => void;
  text: string;
  disabled?: boolean;
};

const FinalizationOption = ({ selected, setSelected, text, disabled }: Props) => {
  return (
    <button
      className={`focus:border-brand600 flex flex-row items-center gap-2 rounded-xl border-2 bg-gray-50 p-4 outline-hidden focus:outline-hidden ${selected ? "border-brand600" : "border-gray-300"} ${disabled ? "cursor-not-allowed" : ""}`}
      onClick={() => setSelected()}
      disabled={disabled}
    >
      {selected ? <SelectedCircleIcon /> : <div className="rounded-full border p-2"></div>}
      <span className="text-left text-base font-semibold text-gray-800">{text}</span>
    </button>
  );
};
